import { ActionButtonType, UserCoverStyle } from "authory-api-types/dist/enums";
import { ArticlePreviewResponse, Collection, ContentResponse, UserResponse } from "authory-api-types/dist/types";
import { useFormik } from "formik";
import React from "react";
import { copyTextToClipboard } from "../../utils/copyToClipboard";
import { EmbedMode, EmbedTheme } from "../../utils/embeds";
import { V3Button } from "../Button";
import { V3CheckBox } from "../OptionInput";
import { V3CaptionGrey35 } from "../ReusableStyledComponents/V3CaptionGrey35";
import { V3Select } from "../Select";
import { V3TextArea, V3TextInput } from "../TextInput/V3TextInput";
import { V3ToggleWithLabel } from "../ToggleSwitch";
import { V3EmbedCollection } from "../V3EmbedCollection";
import { V3EmbedContent } from "../V3EmbedContent";
import { V3EmbedProfile } from "../V3EmbedProfile";
import { V3ModalBase } from "../V3ModalBase";
import { VerticalSpacing } from "../VerticalSpacing";
import { V3EmbedColorOption } from "./V3EmbedColorOption";
import { EmbedRightSidePanel, V3EmbedModalLayout, V3EmbedRadioInputs, V3ShowCodeWrapper } from "./V3EmbedModal.styles";
import {
    computeEmbedCollectiontHeight,
    computeEmbedContentHeight,
    computeEmbedProfileHeight,
    EmbedTarget,
    getEmbedTarget,
    modeOptions
} from "./V3EmbedModal.utils";

interface CreateEmbedFormik {
    showBorder: boolean;
    showHeader: boolean;
    mode: EmbedMode;
    width: number;
    showCode: boolean;
    theme: EmbedTheme;
    numItems: string;
    showSocialMedia: boolean;
    showHighlight: boolean;
    useCover: boolean;
}

interface V3EmbedModalProps {
    modalOpen: boolean;
    onCloseHandler: () => void;
    target: EmbedTarget;
    author: UserResponse;
    item?: ContentResponse;
    collection?: Collection;
    items?: ArticlePreviewResponse[];
}

export const V3EmbedModal = ({ modalOpen, onCloseHandler, target, author, item, collection, items = [] }: V3EmbedModalProps) => {
    const maxItems = Math.min(10, items.length);

    const IS_CONTENT = target === EmbedTarget.CONTENT;
    const IS_COLLECTION = target === EmbedTarget.COLLECTION;
    const IS_PORTFOLIO = target === EmbedTarget.PORTFOLIO;

    const hasContactAction = author.actionButtonType === ActionButtonType.Contact;
    const hasCustomAction = author.actionButtonType === ActionButtonType.Custom;
    const hasAction = hasContactAction || hasCustomAction;
    const hasSocialMedia = !!author.profileComponents.length;
    const hasBackgroundImage = author.coverUrl.length > 1 && author.coverStyle !== UserCoverStyle.none;

    const embedFormik = useFormik<CreateEmbedFormik>({
        enableReinitialize: true,
        initialValues: {
            showBorder: true,
            showHeader: true,
            mode: EmbedMode.normal,
            width: 100,
            showCode: false,
            theme: EmbedTheme.light,
            numItems: `${Math.min(3, maxItems)}`,
            showSocialMedia: hasSocialMedia,
            showHighlight: hasAction,
            useCover: hasBackgroundImage
        },
        onSubmit: () => {}
    });

    const useSeparator = (parseInt(embedFormik.values.numItems) > 0 && !embedFormik.values.useCover) || !embedFormik.values.useCover;

    //@ts-ignore
    let params = "";
    let embedHeight = 0;

    if (IS_CONTENT) {
        params = `?theme=${embedFormik.values.theme}&mode=${embedFormik.values.mode}&showHeader=${embedFormik.values.showHeader}`;
        embedHeight = computeEmbedContentHeight(
            embedFormik.values.showHeader,
            embedFormik.values.mode,
            embedFormik.values.mode === EmbedMode.normal ? !!item?.previewImage : !!item?.thumbnailImage
        );
    }

    if (IS_COLLECTION) {
        params = `?theme=${embedFormik.values.theme}&showHeader=${embedFormik.values.showHeader}&numItems=${embedFormik.values.numItems}`;
        embedHeight = computeEmbedCollectiontHeight(
            embedFormik.values.showHeader,
            !!collection?.description?.length,
            items.length ? parseInt(embedFormik.values.numItems) : 0
        );
    }

    if (IS_PORTFOLIO) {
        params = `?theme=${embedFormik.values.theme}&numItems=${embedFormik.values.numItems}&mode=${embedFormik.values.mode}&showSocialMedia=${embedFormik.values.showSocialMedia}&showHighlight=${embedFormik.values.showHighlight}&useCover=${embedFormik.values.useCover}`;
        embedHeight = computeEmbedProfileHeight(
            embedFormik.values.mode,
            embedFormik.values.showSocialMedia || embedFormik.values.showHighlight,
            !!author.avatarUrl.length,
            useSeparator,
            items.length ? parseInt(embedFormik.values.numItems) : 0,
            embedFormik.values.useCover,
            !!author.bio.length
        );
    }

    const iFrameText = `<iframe referrerpolicy="origin" scrolling="no" src="${getEmbedTarget(target, author, item, collection)}${params}" width="${embedFormik.values.width}%" height="${embedHeight}px" style="border-radius:18px;${embedFormik.values.showBorder ? "border:1px solid #DDDFE3" : ""}" ></iframe>`;

    const onClickHandler = () => {
        copyTextToClipboard(iFrameText, "Embed copied");
    };

    return (
        <V3ModalBase
            isOpen={modalOpen}
            onClose={onCloseHandler}
            title="Create embed widget"
            width="900px"
            useHalfContentSplit={true}
            customHalfSplit="calc(100% - 394px)"
        >
            <V3EmbedModalLayout>
                <div>
                    <VerticalSpacing bottom={16}>
                        <V3EmbedRadioInputs>
                            <div>
                                <V3EmbedColorOption
                                    label="Light"
                                    name="theme"
                                    checked={embedFormik.values.theme === EmbedTheme.light}
                                    value={EmbedTheme.light}
                                    onChange={embedFormik.handleChange}
                                    svg={<img src="/authory_static_files/images/embeds/light_theme.svg" />}
                                />
                            </div>
                            <div>
                                <V3EmbedColorOption
                                    label="Dark"
                                    name="theme"
                                    value={EmbedTheme.dark}
                                    checked={embedFormik.values.theme === EmbedTheme.dark}
                                    onChange={embedFormik.handleChange}
                                    svg={<img src="/authory_static_files/images/embeds/dark_theme.svg" />}
                                />
                            </div>
                        </V3EmbedRadioInputs>
                    </VerticalSpacing>
                    <VerticalSpacing bottom={16}>
                        <V3ToggleWithLabel
                            name="showBorder"
                            checked={embedFormik.values.showBorder}
                            onChange={embedFormik.handleChange}
                            label="Show border"
                        />
                    </VerticalSpacing>
                    {!IS_PORTFOLIO && (
                        <VerticalSpacing bottom={16}>
                            <V3ToggleWithLabel
                                name="showHeader"
                                checked={embedFormik.values.showHeader}
                                onChange={embedFormik.handleChange}
                                label="Show header"
                            />
                        </VerticalSpacing>
                    )}
                    {IS_PORTFOLIO && hasSocialMedia && (
                        <VerticalSpacing bottom={16}>
                            <V3ToggleWithLabel
                                name="showSocialMedia"
                                checked={embedFormik.values.showSocialMedia}
                                onChange={embedFormik.handleChange}
                                label="Show social media"
                            />
                        </VerticalSpacing>
                    )}
                    {IS_PORTFOLIO && hasAction && (
                        <VerticalSpacing bottom={16}>
                            <V3ToggleWithLabel
                                name="showHighlight"
                                checked={embedFormik.values.showHighlight}
                                onChange={embedFormik.handleChange}
                                label="Show Highlight Button"
                            />
                        </VerticalSpacing>
                    )}
                    {IS_PORTFOLIO && hasBackgroundImage && (
                        <VerticalSpacing bottom={16}>
                            <V3ToggleWithLabel
                                name="useCover"
                                checked={embedFormik.values.useCover}
                                onChange={embedFormik.handleChange}
                                label="Show portfolio cover"
                            />
                        </VerticalSpacing>
                    )}
                    {(IS_CONTENT || IS_PORTFOLIO) && (
                        <VerticalSpacing bottom={16}>
                            <V3Select
                                label="Height"
                                name="mode"
                                value={embedFormik.values.mode}
                                onChange={embedFormik.handleChange}
                                options={modeOptions}
                            />
                        </VerticalSpacing>
                    )}
                    {(IS_COLLECTION || IS_PORTFOLIO) && (
                        <VerticalSpacing bottom={16}>
                            <V3Select
                                label="Number of items"
                                name="numItems"
                                value={embedFormik.values.numItems}
                                onChange={embedFormik.handleChange}
                                options={Array.from(Array(maxItems + 1).keys()).map((item) => {
                                    const val = `${item}`;
                                    return { value: val, text: val };
                                })}
                            />
                        </VerticalSpacing>
                    )}
                    <VerticalSpacing bottom={24}>
                        <V3TextInput
                            label="Width (%)"
                            name="width"
                            type="number"
                            value={embedFormik.values.width}
                            onChange={embedFormik.handleChange}
                            max="100"
                            min="50"
                        />
                    </VerticalSpacing>
                    <V3ShowCodeWrapper>
                        <div>
                            <V3CheckBox
                                onChange={embedFormik.handleChange}
                                name="showCode"
                                id="showCode"
                                label="Show code"
                                checked={embedFormik.values.showCode}
                            />
                        </div>
                        <div>
                            <V3Button text="Copy" autoWidth onClick={onClickHandler} />
                        </div>
                    </V3ShowCodeWrapper>
                    {embedFormik.values.showCode && (
                        <VerticalSpacing top={24}>
                            <div onClick={onClickHandler}>
                                <V3TextArea style={{ wordBreak: "break-all", maxHeight: 103 }} disabled value={iFrameText} />
                            </div>
                        </VerticalSpacing>
                    )}
                </div>
                <EmbedRightSidePanel>
                    <div>
                        <VerticalSpacing bottom={18}>
                            <V3CaptionGrey35>Preview of your embed widget</V3CaptionGrey35>
                        </VerticalSpacing>
                    </div>
                    <div>
                        <VerticalSpacing bottom={18}>
                            <div
                                style={{
                                    border: embedFormik.values.showBorder ? "1px solid #DDDFE3" : "",
                                    borderRadius: "18px",
                                    height: embedHeight,
                                    overflow: "hidden",
                                    width: `${embedFormik.values.width}%`,
                                    margin: "0 auto"
                                }}
                            >
                                {!!item && IS_CONTENT && (
                                    <V3EmbedContent
                                        author={author}
                                        content={item}
                                        embedMode={embedFormik.values.mode}
                                        embedTheme={embedFormik.values.theme}
                                        showHeader={embedFormik.values.showHeader}
                                    />
                                )}
                                {!!collection && !!items.length && IS_COLLECTION && (
                                    <V3EmbedCollection
                                        author={author}
                                        collection={collection}
                                        embedTheme={embedFormik.values.theme}
                                        showHeader={embedFormik.values.showHeader}
                                        items={items.slice(0, parseInt(embedFormik.values.numItems))}
                                    />
                                )}
                                {IS_PORTFOLIO && (
                                    <V3EmbedProfile
                                        author={author}
                                        embedMode={embedFormik.values.mode}
                                        embedTheme={embedFormik.values.theme}
                                        items={items.slice(0, parseInt(embedFormik.values.numItems))}
                                        showSocialMedia={embedFormik.values.showSocialMedia}
                                        showHighlight={embedFormik.values.showHighlight}
                                        useCover={embedFormik.values.useCover}
                                    />
                                )}
                            </div>
                        </VerticalSpacing>
                    </div>
                    <div></div>
                </EmbedRightSidePanel>
            </V3EmbedModalLayout>
        </V3ModalBase>
    );
};
